import path from 'path'

export function addTrailingSlash(pathStr: string = '/') {
  if (pathStr.endsWith('/')) {
    return pathStr
  }
  return `${pathStr}/`
}

export function removeTrailingSlash(pathStr: string = '/') {
  if (!pathStr.endsWith('/') || /^[./]*$/.test(pathStr)) {
    return pathStr
  }
  return pathStr.replace(/^(.+)\/+$/, '$1')
}

/**
 * Get depth of path
 * @example
 * const depth = getPathDepth('/sync')
 * // depth == 1
 * @param pathStr A file path
 * @returns The depth of the path
 */
export function getPathDepth(pathStr: string) {
  return (addTrailingSlash(makeAbsolute(pathStr)).match(/\//g)?.length || 1) - 1
}
export function getFilenameFromPath(pathStr: string) {
  return path.basename(pathStr)
}
export function getParentPath(pathStr = '/') {
  return path.join('/', path.normalize(pathStr), '..')
}
export function renamePath(pathStr = '/', name = '') {
  const oldName = path.basename(pathStr)
  if (!oldName) {
    return pathStr
  }
  return path.join('/', path.dirname(pathStr), path.basename(name))
}
export function makeAbsolute(pathStr = '') {
  return removeTrailingSlash(path.join('/', path.normalize(pathStr)))
}
export function makeRelative(pathStr = '') {
  return makeAbsolute(pathStr).substring(1)
}

export function addPrefixToPath(pathStr: string, prefix: string) {
  const prefixPath = makeAbsolute(prefix)
  const absPath = makeAbsolute(pathStr)
  if (!absPath.startsWith(prefixPath)) {
    return path.join(prefixPath, absPath)
  }
  return absPath
}
export function removePrefixFromPath(pathStr: string, prefix: string) {
  const prefixPath = makeAbsolute(prefix)
  const absPath = makeAbsolute(pathStr)
  if (absPath.startsWith(prefixPath)) {
    return makeAbsolute(absPath.slice(prefixPath.length))
  }
  return absPath
}

/**
 * Runs encodeURIComponent on each element in a path,  returning original pathname if
 * decoding throws
 * @param pathname
 * @returns Encoded path
 */
export function uriEncodePath(pathname: string) {
  return pathname.replaceAll(/[^/]+/g, safeEncodeURIComponent)
}
/**
 * Runs decodeURIComponent on pathname, returning original pathname if decoding throws
 * @param pathname
 * @returns Decoded path
 */
export function uriDecodePath(pathname: string) {
  return pathname.replaceAll(/[^/]+/g, safeDecodeURIComponent)
}
export function safeEncodeURIComponent(pathname: string) {
  try {
    return encodeURIComponent(pathname)
  } catch (error) {
    console.warn(
      'encodeURIComponent %s failed, returning unmodified string',
      pathname,
      error,
    )
    return pathname
  }
}
export function safeDecodeURIComponent(pathname: string) {
  try {
    return decodeURIComponent(pathname)
  } catch (error) {
    console.warn(
      'decodeURIComponent %s failed, returning unmodified string',
      pathname,
      error,
    )
    return pathname
  }
}

export function pathToArray(pathStr: string) {
  const p = makeAbsolute(pathStr)
  if (p === '/') {
    return ['']
  }
  return p.split('/')
}
export function arrayToPath(arr: string[], start?: number, end?: number) {
  return makeAbsolute(arr.slice(start, end).join('/'))
}
export function createPath(...items: string[]) {
  return removeTrailingSlash(path.join('/', ...items))
}
export function createRelativePath(...items: string[]) {
  return `.${path.join('/', ...items)}`
}

export function popPath(pathStr: string, levels = 1) {
  const parentPath = pathToArray(pathStr)
  return arrayToPath(parentPath, 0, -Math.abs(levels))
}

/**
 * Remove first n items from path
 * @param pathStr File path
 * @param levels Number of items to remove
 * @returns Shifted path
 */
export function shiftPath(pathStr: string, levels = 1) {
  return createPath(...pathToArray(pathStr).slice(Math.abs(levels + 1)))
}
export function relativeShiftPath(pathStr: string, levels = 1) {
  return makeRelative(arrayToPath(pathToArray(pathStr), Math.abs(levels + 1)))
}
